import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Loader2, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const INDUSTRIES = ["Restaurant", "Hotel", "Healthcare", "Warehouse", "Retail", "Church", "Commercial Cleaning", "Other"];
const INTERESTS = ["AI Employees", "Cleaning Robots", "Service Robots", "Warehouse Robots", "Not Sure"];

const schema = z.object({
  name: z.string().trim().min(1),
  email: z.string().trim().email(),
  phone: z.string().trim().max(40).optional(),
  business_name: z.string().trim().max(200).optional(),
  industry: z.string().optional(),
  monthly_labor_cost: z.coerce.number().min(0),
  repetitive_hours: z.coerce.number().min(0),
  facility_size: z.string().optional(),
  interest: z.string().optional(),
});

const ROICalculatorPage = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_name: "",
    industry: "", monthly_labor_cost: "", repetitive_hours: "",
    facility_size: "", interest: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ low: number; high: number } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message || "Please complete the form");
      return;
    }
    setLoading(true);
    // Conservative estimate: 20-40% of repetitive labor cost
    const hourly = 22; // fully-loaded blended
    const monthlyRepetitive = parsed.data.repetitive_hours * 4.33 * hourly;
    const low = Math.round(monthlyRepetitive * 0.25);
    const high = Math.round(monthlyRepetitive * 0.45);

    const { error } = await supabase.from("roi_leads").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone,
      business_name: parsed.data.business_name,
      industry: parsed.data.industry,
      monthly_labor_cost: parsed.data.monthly_labor_cost,
      repetitive_hours: parsed.data.repetitive_hours,
      facility_size: parsed.data.facility_size,
      interest: parsed.data.interest,
      estimated_savings: Math.round((low + high) / 2),
    });
    setLoading(false);
    if (error) {
      toast.error("Could not save. Please try again.");
      return;
    }
    setResult({ low, high });
  };

  return (
    <>
      <SEO title="ROI Calculator | Neil AI Global Solutions" description="Estimate your monthly savings from AI automation and PUDU robotics. Free calculator with personalized results." canonical="https://www.neilai.ai/roi-calculator" />
      <div className="min-h-screen bg-[#05070A] text-white pb-24">
        <Navigation />
        <section className="pt-32 md:pt-40 pb-16">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-10">
              <Calculator className="w-12 h-12 text-[#00F5A0] mx-auto mb-4" />
              <h1 className="text-4xl md:text-5xl font-bold mb-3">ROI Calculator</h1>
              <p className="text-slate-300">See an honest estimate of what AI Employees and PUDU robots could save you each month.</p>
            </div>

            <Card className="bg-[#0A1828]/60 border-primary/20 p-6 md:p-8">
              {result ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-14 h-14 text-[#00F5A0] mx-auto mb-4" />
                  <p className="text-slate-300 mb-2">Estimated monthly savings</p>
                  <p className="text-5xl font-bold bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] bg-clip-text text-transparent">
                    ${result.low.toLocaleString()} – ${result.high.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-400 mt-3 mb-8">Based on conservative labor offset of 25–45% on the repetitive hours you provided.</p>
                  <p className="text-slate-300 mb-6">A Neil AI specialist will follow up with a tailored breakdown.</p>
                  <Button asChild size="lg" className="bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold"><a href="/book-demo">Book a Free Demo</a></Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                    <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                    <div><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Industry</Label>
                      <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                        <SelectTrigger className="bg-[#05070A] border-primary/30"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Interested in</Label>
                      <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                        <SelectTrigger className="bg-[#05070A] border-primary/30"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{INTERESTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Current monthly labor cost ($)</Label><Input type="number" required value={form.monthly_labor_cost} onChange={(e) => setForm({ ...form, monthly_labor_cost: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                    <div><Label>Repetitive hours / week</Label><Input type="number" required value={form.repetitive_hours} onChange={(e) => setForm({ ...form, repetitive_hours: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                  </div>
                  <div><Label>Facility size (sq ft, optional)</Label><Input value={form.facility_size} onChange={(e) => setForm({ ...form, facility_size: e.target.value })} className="bg-[#05070A] border-primary/30" /></div>
                  <Button type="submit" disabled={loading} size="lg" className="w-full bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold h-12">
                    {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Calculator className="w-5 h-5 mr-2" />}
                    {loading ? "Calculating…" : "Calculate My ROI"}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">We'll email you a copy of your personalized results.</p>
                </form>
              )}
            </Card>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};
export default ROICalculatorPage;
